import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  BarChart3,
  Percent,
  Wallet,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import useStore from '../../store/useStore';
import { Card, KPICard } from '../../components/ui/Card';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { formatCurrency, formatPercent, formatNumber } from '../../utils/format';
import { fetchQuotes } from '../../utils/fetchQuotes';
import { fetchTickerInfo } from '../../utils/fetchTickerInfo';
import { fetchAllHoldings } from '../../utils/fetchHoldings';
import { computeYtdContributions } from '../../data/balanceSheetHistory';

const PIE_COLORS = ['#2563EB', '#10B981', '#D97706', '#EF4444', '#8B5CF6', '#EC4899'];

// ─── Geographic classification for known tickers ─────────────────────────────
// Each ticker maps to { domestic, international } as fractions summing to 1.
// CASH/bonds are handled separately.
const GEO_MAP = {
  // US total / large cap / dividend
  VTI: { domestic: 1, international: 0 },
  VOO: { domestic: 1, international: 0 },
  SCHD: { domestic: 1, international: 0 },
  QQQM: { domestic: 1, international: 0 },
  QQQ: { domestic: 1, international: 0 },
  VIG: { domestic: 1, international: 0 },
  VITSX: { domestic: 1, international: 0 },
  FNSFX: { domestic: 1, international: 0 },
  VFIAX: { domestic: 1, international: 0 },
  SWTSX: { domestic: 1, international: 0 },
  SPY: { domestic: 1, international: 0 },
  IWM: { domestic: 1, international: 0 },
  VUG: { domestic: 1, international: 0 },
  VTV: { domestic: 1, international: 0 },
  SCHB: { domestic: 1, international: 0 },
  // International
  VXUS: { domestic: 0, international: 1 },
  VEA: { domestic: 0, international: 1 },
  VWO: { domestic: 0, international: 1 },
  ILF: { domestic: 0, international: 1 },
  EFA: { domestic: 0, international: 1 },
  EEM: { domestic: 0, international: 1 },
  IXUS: { domestic: 0, international: 1 },
  // Blended
  VT: { domestic: 0.6, international: 0.4 },
  ACWI: { domestic: 0.6, international: 0.4 },
};

const CONTRIB_KEYS = [
  { key: 'contribRothIra', label: 'IRA', limit: 7500 },
  { key: 'contribHsa', label: 'HSA', limit: 4300 },
  { key: 'contrib401k', label: '401(k)', limit: 23500 },
  { key: 'contribBrokerage', label: 'Brokerage', limit: null },
];

const INPUT_CLASS =
  'w-full bg-bg-tertiary border border-border/50 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent';

const EMPTY_FORM = {
  account: '',
  ticker: '',
  name: '',
  shares: '',
  costBasis: '',
  cashValue: '',
};


const RADIAN = Math.PI / 180;
function renderOuterPercentLabel({ cx, cy, midAngle, outerRadius, percent }) {
  const radius = outerRadius + 18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.03) return null; // hide tiny slices
  return (
    <text x={x} y={y} fill="#8B949E" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const data = payload[0];
  return (
    <div className="bg-bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-text-primary text-sm font-medium">{data.name}</p>
      <p className="text-text-secondary text-xs">
        {formatCurrency(data.value)} ({((data.value / data.payload.total) * 100).toFixed(1)}%)
      </p>
    </div>
  );
}

function ProgressBar({ label, current, max, color = 'bg-accent' }) {
  const hasLimit = max != null && max > 0;
  const pct = hasLimit ? Math.min((current / max) * 100, 100) : 0;
  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-text-primary text-sm font-medium">{label}</span>
        <span className="text-text-secondary text-xs font-mono tabular-nums">
          {formatCurrency(current)}{hasLimit ? ` / ${formatCurrency(max)}` : ''}
        </span>
      </div>
      {hasLimit && (
        <>
          <div className="w-full h-2.5 bg-bg-tertiary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${color}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-text-muted text-xs mt-1 text-right">
            {formatCurrency(Math.max(max - current, 0))} remaining ({pct.toFixed(0)}%)
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Safely evaluate simple arithmetic expressions (e.g. "1000+650", "50.5*3", "100-25").
 * Supports +, -, *, / and parentheses. Returns NaN for invalid or unsafe input.
 */
function evalMathExpr(str) {
  const s = String(str).replace(/\s/g, '');
  // Only allow digits, decimal points, and basic math operators
  if (!/^[\d.+\-*/()]+$/.test(s)) return NaN;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function(`"use strict"; return (${s});`)();
    return typeof result === 'number' && isFinite(result) ? result : NaN;
  } catch {
    return NaN;
  }
}

function InlineEditCell({ value, displayValue, align = 'right', className = '', onSave, step = 'any' }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select();
    }
  }, [editing]);

  const save = () => {
    const num = evalMathExpr(editVal);
    if (!isNaN(num) && num !== value) {
      onSave(num);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <Td align={align}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={editVal}
          onChange={(e) => setEditVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') { setEditing(false); setEditVal(value); }
          }}
          className="w-28 bg-bg-tertiary border border-accent rounded px-2 py-1 text-sm text-text-primary font-mono text-right focus:outline-none"
          autoFocus
        />
      </Td>
    );
  }

  return (
    <Td
      align={align}
      className={`${className} cursor-pointer hover:bg-accent/10 transition-colors rounded`}
      onClick={() => { setEditing(true); setEditVal(value); }}
      title="Click to edit"
    >
      {displayValue}
    </Td>
  );
}

export default function Investments() {
  const investments = useStore((s) => s.investments);
  const accounts = useStore((s) => s.accounts);
  const balanceSheetMonthly = useStore((s) => s.balanceSheetMonthly);
  const addInvestment = useStore((s) => s.addInvestment);
  const updateInvestment = useStore((s) => s.updateInvestment);
  const removeInvestment = useStore((s) => s.removeInvestment);
  const updateAccount = useStore((s) => s.updateAccount);
  const addAccount = useStore((s) => s.addAccount);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fetchedPrice, setFetchedPrice] = useState(null);
  const [fetchedYield, setFetchedYield] = useState(null);
  const [fetchedAssetClass, setFetchedAssetClass] = useState(null);
  const [fetchingTicker, setFetchingTicker] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Sync a single account's balance from its investment holdings
  const syncAccountBalance = useCallback((accountName, overrides = {}) => {
    const accountTotal = investments.reduce((sum, inv) => {
      if (inv.account !== accountName) return sum;
      // Use override value if this investment was just updated
      if (overrides[inv.id] != null) return sum + overrides[inv.id];
      return sum + inv.currentValue;
    }, 0);
    const matchingAccount = accounts.find(
      (a) => a.type === 'investment' && a.name === accountName
    );
    if (matchingAccount) {
      updateAccount(matchingAccount.id, { balance: Math.round(accountTotal * 100) / 100 });
    }
  }, [investments, accounts, updateAccount]);

  // Save an inline edit (shares or costBasis) and sync the account balance
  const handleSaveEdit = useCallback(
    async (investmentId, field, newValue) => {
      const investment = investments.find((i) => i.id === investmentId);
      if (!investment) return;
      const merged = { ...investment, [field]: newValue };
      const newCurrentValue = merged.shares * merged.currentPrice;
      await updateInvestment(investmentId, { [field]: newValue });
      syncAccountBalance(investment.account, { [investmentId]: newCurrentValue });
    },
    [investments, updateInvestment, syncAccountBalance]
  );

  // Fetch live prices from Yahoo Finance and update all holdings
  const handleRefreshPrices = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const tickers = [...new Set(investments.map((i) => i.ticker))];
      const quotes = await fetchQuotes(tickers);
      const updatedCount = Object.keys(quotes).length;
      if (updatedCount === 0) {
        console.warn('No price data returned — Yahoo Finance may be unavailable');
        return;
      }

      // Update each investment that got a new price
      const overridesByAccount = {}; // { accountName: { invId: newValue } }
      for (const inv of investments) {
        const quote = quotes[inv.ticker?.toUpperCase()];
        if (quote?.currentPrice != null) {
          await updateInvestment(inv.id, { currentPrice: quote.currentPrice });
          const newValue = inv.shares * quote.currentPrice;
          if (!overridesByAccount[inv.account]) overridesByAccount[inv.account] = {};
          overridesByAccount[inv.account][inv.id] = newValue;
        }
      }

      // Sync all affected account balances
      for (const [accountName, overrides] of Object.entries(overridesByAccount)) {
        syncAccountBalance(accountName, overrides);
      }

      setLastRefresh(new Date());
      localStorage.setItem('meridian:lastPriceRefresh', String(Date.now()));
    } catch (err) {
      console.error('Error refreshing prices:', err);
    } finally {
      setRefreshing(false);
    }
  }, [investments, refreshing, updateInvestment, syncAccountBalance]);

  // Auto-refresh prices on page load if stale (>1 hour since last refresh)
  const autoRefreshRan = useRef(false);
  useEffect(() => {
    if (autoRefreshRan.current || investments.length === 0) return;
    autoRefreshRan.current = true;
    // Check localStorage for last refresh timestamp
    const lastTs = localStorage.getItem('meridian:lastPriceRefresh');
    const staleMs = 60 * 60 * 1000; // 1 hour
    if (!lastTs || Date.now() - Number(lastTs) > staleMs) {
      handleRefreshPrices().then(() => {
        localStorage.setItem('meridian:lastPriceRefresh', String(Date.now()));
      });
    } else {
      setLastRefresh(new Date(Number(lastTs)));
    }
  }, [investments.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // YTD contributions (sum all months in current year, merging Supabase + historical seed)
  const currentYear = new Date().getFullYear();
  const ytdContributions = useMemo(
    () => computeYtdContributions(balanceSheetMonthly, currentYear),
    [balanceSheetMonthly, currentYear],
  );

  // ─── ETF/Fund holdings breakdown ─────────────────────────────────────────
  const [holdingsData, setHoldingsData] = useState({});
  const [loadingHoldings, setLoadingHoldings] = useState(false);

  // Fetch holdings on mount (once per session)
  useEffect(() => {
    if (investments.length === 0) return;
    const tickers = [...new Set(investments.map((i) => i.ticker))];
    let cancelled = false;
    setLoadingHoldings(true);
    fetchAllHoldings(tickers).then((data) => {
      if (!cancelled) {
        setHoldingsData(data);
        setLoadingHoldings(false);
      }
    });
    return () => { cancelled = true; };
  }, [investments.length]); // only re-fetch when count changes

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isCashTicker = /^(CASH|MARGIN)$/i.test(form.ticker?.trim());

  // Auto-fetch price, yield, and asset class when ticker field loses focus
  const handleTickerBlur = useCallback(async () => {
    const ticker = form.ticker?.trim().toUpperCase();
    if (!ticker) return;
    // CASH/MARGIN don't have market prices — auto-set defaults
    if (/^(CASH|MARGIN)$/.test(ticker)) {
      setFetchedPrice(1);
      setFetchedYield(0);
      setFetchedAssetClass('Cash');
      if (!form.name) handleFormChange('name', ticker === 'MARGIN' ? 'Margin' : 'Cash');
      return;
    }
    setFetchingTicker(true);
    setFetchedPrice(null);
    setFetchedYield(null);
    setFetchedAssetClass(null);
    try {
      const info = await fetchTickerInfo(ticker);
      if (info) {
        if (info.currentPrice != null) setFetchedPrice(info.currentPrice);
        if (info.yieldRate != null) setFetchedYield(info.yieldRate);
        if (info.assetClass) setFetchedAssetClass(info.assetClass);
      }
    } catch (err) {
      console.warn('Failed to fetch ticker info for', ticker, err);
    } finally {
      setFetchingTicker(false);
    }
  }, [form.ticker, form.name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.account || !form.ticker || !form.name || !fetchedPrice) return;
    const accountName = form.account;
    const ticker = form.ticker.trim().toUpperCase();
    const isCash = /^(CASH|MARGIN)$/.test(ticker);
    const shares = isCash ? (evalMathExpr(form.cashValue) || 0) : (evalMathExpr(form.shares) || 0);
    const currentPrice = isCash ? 1 : fetchedPrice;
    const costBasis = isCash ? 1 : (evalMathExpr(form.costBasis) || 0);
    const yieldRate = isCash ? 0 : (fetchedYield || 0);
    const assetClass = isCash ? 'Cash' : (fetchedAssetClass || 'US Equities');

    // Auto-create investment account if it doesn't exist
    const existingAccount = accounts.find(
      (a) => a.type === 'investment' && a.name === accountName
    );
    if (!existingAccount) {
      await addAccount({
        name: accountName,
        type: 'investment',
        balance: 0,
        institution: '',
        asOfDate: new Date().toISOString().slice(0, 10),
      });
    }

    // Add the investment
    await addInvestment({
      account: accountName,
      ticker: form.ticker.toUpperCase(),
      name: form.name,
      assetClass,
      shares,
      costBasis,
      currentPrice,
      yieldRate,
    });

    // Sync account balance (sum existing + new holding)
    const newHoldingValue = shares * currentPrice;
    const existingTotal = investments
      .filter((inv) => inv.account === accountName)
      .reduce((sum, inv) => sum + inv.currentValue, 0);
    const newTotal = Math.round((existingTotal + newHoldingValue) * 100) / 100;
    const latestAccounts = useStore.getState().accounts;
    const acct = latestAccounts.find((a) => a.type === 'investment' && a.name === accountName);
    if (acct) {
      updateAccount(acct.id, { balance: newTotal });
    }

    setForm(EMPTY_FORM);
    setFetchedPrice(null);
    setFetchedYield(null);
    setFetchedAssetClass(null);
    setShowForm(false);
  };

  // Delete a holding and re-sync the account balance
  const handleDeleteHolding = useCallback(async (holding) => {
    const accountName = holding.account;
    await removeInvestment(holding.id);
    // Re-sync: sum remaining holdings (excluding the deleted one)
    const remaining = investments.filter(
      (inv) => inv.account === accountName && inv.id !== holding.id
    );
    const newTotal = Math.round(
      remaining.reduce((sum, inv) => sum + inv.currentValue, 0) * 100
    ) / 100;
    const matchingAccount = accounts.find(
      (a) => a.type === 'investment' && a.name === accountName
    );
    if (matchingAccount) {
      updateAccount(matchingAccount.id, { balance: newTotal });
    }
  }, [investments, accounts, removeInvestment, updateAccount]);

  const {
    totalValue,
    totalCostBasis,
    totalGainLoss,
    totalGainLossPct,
    totalAnnualDividend,
    currentYield,
    allocationData,
    fullPortfolioValue,
    balanceSheetCash,
    accountSummaries,
  } = useMemo(() => {
    const totalValue = investments.reduce((sum, h) => sum + h.currentValue, 0);
    const totalCostBasis = investments.reduce(
      (sum, h) => sum + h.shares * h.costBasis,
      0
    );
    const totalGainLoss = totalValue - totalCostBasis;
    const totalGainLossPct =
      totalCostBasis !== 0 ? (totalGainLoss / totalCostBasis) * 100 : 0;
    const totalAnnualDividend = investments.reduce(
      (sum, h) => sum + h.annualDividend,
      0
    );
    const currentYield =
      totalValue !== 0 ? (totalAnnualDividend / totalValue) * 100 : 0;

    // Balance sheet cash (checking, savings, cash accounts)
    const CASH_TYPES = ['cash', 'checking', 'savings'];
    const balanceSheetCash = accounts
      .filter((a) => CASH_TYPES.includes(a.type) && (a.balance || 0) > 0)
      .reduce((sum, a) => sum + (a.balance || 0), 0);

    // Full portfolio = investment holdings + balance sheet cash
    const fullPortfolioValue = totalValue + balanceSheetCash;

    // Allocation by account (Roth IRA, 401k, HSA, Taxable, Cash Accounts, etc.)
    const allocationMap = {};
    investments.forEach((h) => {
      if (!allocationMap[h.account]) allocationMap[h.account] = 0;
      allocationMap[h.account] += h.currentValue;
    });
    if (balanceSheetCash > 0) {
      allocationMap['Cash Accounts'] = (allocationMap['Cash Accounts'] || 0) + balanceSheetCash;
    }
    const allocationData = Object.entries(allocationMap)
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 100) / 100,
        total: fullPortfolioValue,
      }))
      .sort((a, b) => b.value - a.value);

    // Account summaries from investments + balance sheet cash
    const accountMap = {};
    investments.forEach((h) => {
      if (!accountMap[h.account]) accountMap[h.account] = 0;
      accountMap[h.account] += h.currentValue;
    });
    // Add balance sheet cash as its own entry
    if (balanceSheetCash > 0) {
      accountMap['Cash Accounts'] = balanceSheetCash;
    }
    const accountSummaries = Object.entries(accountMap)
      .map(([name, value]) => ({ name, value: Math.round(value * 100) / 100 }))
      .sort((a, b) => b.value - a.value);

    return {
      totalValue,
      totalCostBasis,
      totalGainLoss,
      totalGainLossPct,
      totalAnnualDividend,
      currentYield,
      allocationData,
      fullPortfolioValue,
      balanceSheetCash,
      accountSummaries,
    };
  }, [investments, accounts]);

  const contributionTrackers = CONTRIB_KEYS.map(({ key, label, limit }) => ({
    label,
    contributed: ytdContributions[key],
    limit,
  }));

  // ─── Geographic allocation (Domestic / International / Fixed Income / Cash) ─
  const geoData = useMemo(() => {
    let domestic = 0;
    let international = 0;
    let fixedIncome = 0;
    let cash = balanceSheetCash;

    investments.forEach((h) => {
      const ticker = h.ticker?.toUpperCase();
      if (ticker === 'CASH') {
        cash += h.currentValue;
        return;
      }
      // Fixed income / bonds
      if (/bond|treasury|fixed|income/i.test(h.assetClass) || /SGOV|BND|AGG|SCHZ|BNDX|TLT|SHY|IEF|VCSH|VCIT/i.test(ticker)) {
        fixedIncome += h.currentValue;
        return;
      }
      const geo = GEO_MAP[ticker];
      if (geo) {
        domestic += h.currentValue * geo.domestic;
        international += h.currentValue * geo.international;
      } else if (/international/i.test(h.assetClass)) {
        international += h.currentValue;
      } else {
        domestic += h.currentValue;
      }
    });

    const total = domestic + international + fixedIncome + cash;
    return [
      { name: 'Domestic', value: Math.round(domestic * 100) / 100, total },
      { name: 'International', value: Math.round(international * 100) / 100, total },
      { name: 'Fixed Income', value: Math.round(fixedIncome * 100) / 100, total },
      { name: 'Cash', value: Math.round(cash * 100) / 100, total },
    ].filter((d) => d.value > 0);
  }, [investments, balanceSheetCash]);

  // ─── Weighted sector breakdown from ETF holdings data ─────────────────────
  const sectorData = useMemo(() => {
    if (Object.keys(holdingsData).length === 0) return [];
    const totalPortfolio = investments.reduce((s, h) => s + h.currentValue, 0);
    if (totalPortfolio === 0) return [];

    const sectorMap = {};
    investments.forEach((h) => {
      const ticker = h.ticker?.toUpperCase();
      const data = holdingsData[ticker];
      if (!data?.sectorWeights?.length) return;
      const weight = h.currentValue / totalPortfolio; // portfolio weight of this holding
      data.sectorWeights.forEach((sw) => {
        if (!sectorMap[sw.sector]) sectorMap[sw.sector] = 0;
        sectorMap[sw.sector] += sw.weight * weight;
      });
    });

    const total = Object.values(sectorMap).reduce((s, v) => s + v, 0);
    return Object.entries(sectorMap)
      .map(([name, value]) => ({
        name,
        value: Math.round(value * 10000) / 100, // convert to percentage
        total: Math.round(total * 10000) / 100,
      }))
      .sort((a, b) => b.value - a.value)
      .filter((d) => d.value > 0.1); // filter out tiny slices
  }, [investments, holdingsData]);

  // ─── Weighted top holdings from ETF data ──────────────────────────────────
  const topHoldingsData = useMemo(() => {
    if (Object.keys(holdingsData).length === 0) return [];
    const totalPortfolio = investments.reduce((s, h) => s + h.currentValue, 0);
    if (totalPortfolio === 0) return [];

    const holdingMap = {};
    investments.forEach((h) => {
      const ticker = h.ticker?.toUpperCase();
      const data = holdingsData[ticker];
      if (!data?.holdings?.length) return;
      const weight = h.currentValue / totalPortfolio;
      data.holdings.forEach((holding) => {
        const key = holding.symbol || holding.name;
        if (!holdingMap[key]) holdingMap[key] = { name: holding.name || holding.symbol, value: 0 };
        holdingMap[key].value += holding.percent * weight;
      });
    });

    const entries = Object.values(holdingMap).sort((a, b) => b.value - a.value);
    const top = entries.slice(0, 15);
    const otherVal = entries.slice(15).reduce((s, e) => s + e.value, 0);
    const total = entries.reduce((s, e) => s + e.value, 0);
    if (otherVal > 0) top.push({ name: 'Other', value: otherVal });

    return top.map((d) => ({
      name: d.name,
      value: Math.round(d.value * 10000) / 100,
      total: Math.round(total * 10000) / 100,
    }));
  }, [investments, holdingsData]);

  return (
    <div>
      <PageHeader title="Investment Summary" subtitle="All accounts">
        <button
          onClick={handleRefreshPrices}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-tertiary text-text-secondary text-xs font-medium rounded-lg hover:text-text-primary transition-colors disabled:opacity-50"
          title="Refresh live prices from Yahoo Finance"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Prices'}
        </button>
<button
          onClick={() => setShowForm((prev) => !prev)}
          className="flex items-center gap-1.5 bg-accent hover:bg-accent/90 text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Holding
        </button>
      </PageHeader>

{/* Last refresh timestamp */}
      {lastRefresh && (
        <p className="text-text-muted text-xs mb-2 text-right">
          Prices updated {lastRefresh.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </p>
      )}

      {/* Collapsible Add Holding Form */}
      {showForm && (
        <Card className="mb-6">
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-text-primary">New Holding</h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-text-muted hover:text-text-primary transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Account</label>
                <input
                  type="text"
                  placeholder="e.g. IRA"
                  value={form.account}
                  onChange={(e) => handleFormChange('account', e.target.value)}
                  className={INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Ticker</label>
                <input
                  type="text"
                  placeholder="e.g. VTI"
                  value={form.ticker}
                  onChange={(e) => handleFormChange('ticker', e.target.value)}
                  onBlur={handleTickerBlur}
                  className={INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Name</label>
                <input
                  type="text"
                  placeholder="e.g. Vanguard Total Stock Market"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className={INPUT_CLASS}
                  required
                />
              </div>
              {isCashTicker ? (
                <div>
                  <label className="block text-xs text-text-secondary mb-1">Value</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={form.cashValue}
                    onChange={(e) => handleFormChange('cashValue', e.target.value)}
                    className={INPUT_CLASS}
                    required
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Shares</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={form.shares}
                      onChange={(e) => handleFormChange('shares', e.target.value)}
                      className={INPUT_CLASS}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Cost Basis</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={form.costBasis}
                      onChange={(e) => handleFormChange('costBasis', e.target.value)}
                      className={INPUT_CLASS}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Current Price</label>
                    {fetchingTicker ? (
                      <div className="flex items-center gap-2 h-[38px]">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-accent" />
                        <span className="text-xs text-text-muted">Fetching...</span>
                      </div>
                    ) : fetchedPrice != null ? (
                      <div className="flex items-center h-[38px] px-3 text-sm font-medium text-success">
                        {formatCurrency(fetchedPrice, 2)}
                      </div>
                    ) : (
                      <div className="flex items-center h-[38px] px-3 text-xs text-text-muted">
                        Enter a ticker to auto-fetch
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Yield Rate</label>
                    {fetchingTicker ? (
                      <div className="flex items-center gap-2 h-[38px]">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-accent" />
                        <span className="text-xs text-text-muted">Fetching...</span>
                      </div>
                    ) : fetchedYield != null ? (
                      <div className="flex items-center h-[38px] px-3 text-sm font-medium text-success">
                        {fetchedYield.toFixed(2)}%
                      </div>
                    ) : (
                      <div className="flex items-center h-[38px] px-3 text-xs text-text-muted">
                        Auto-fetched from ticker
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">Asset Class</label>
                    {fetchingTicker ? (
                      <div className="flex items-center gap-2 h-[38px]">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-accent" />
                        <span className="text-xs text-text-muted">Fetching...</span>
                      </div>
                    ) : fetchedAssetClass ? (
                      <div className="flex items-center h-[38px] px-3 text-sm font-medium text-success">
                        {fetchedAssetClass}
                      </div>
                    ) : (
                      <div className="flex items-center h-[38px] px-3 text-xs text-text-muted">
                        Auto-fetched from ticker
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setForm(EMPTY_FORM);
                  setFetchedPrice(null);
                  setFetchedYield(null);
                  setFetchedAssetClass(null);
                  setShowForm(false);
                }}
                className="text-sm text-text-muted hover:text-text-primary transition-colors px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-accent hover:bg-accent/90 text-white text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
              >
                Add Holding
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 kpi-grid">
        <KPICard
          title="Full Portfolio Value"
          value={formatCurrency(fullPortfolioValue)}
          subtitle={balanceSheetCash > 0 ? `Incl. ${formatCurrency(balanceSheetCash)} cash accts` : undefined}
          icon={DollarSign}
        />
        <KPICard
          title="Total Cost Basis"
          value={formatCurrency(totalCostBasis)}
          icon={Wallet}
        />
        <KPICard
          title="Unrealized Gain/Loss"
          value={formatCurrency(totalGainLoss)}
          change={totalGainLossPct}
          icon={TrendingUp}
        />
        <KPICard
          title="Current Yield"
          value={`${currentYield.toFixed(2)}%`}
          icon={Percent}
        />
      </div>

      {/* Donut Chart + Account Summaries Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 kpi-grid">
        {/* Allocation Donut */}
        <Card className="lg:col-span-1">
          <h3 className="text-sm font-semibold text-text-primary mb-4">
            Account Allocation
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={allocationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                  label={renderOuterPercentLabel}
                  labelLine={false}
                >
                  {allocationData.map((_, index) => (
                    <Cell
                      key={index}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, color: '#8B949E' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Account Summary Cards */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="text-sm font-semibold text-text-primary">
            Account Values
          </h3>
          {accountSummaries.map((acct) => (
            <Card key={acct.name} className="flex items-center justify-between">
              <span className="text-text-primary text-sm font-medium">
                {acct.name}
              </span>
              <span className="text-text-primary text-sm font-mono font-semibold">
                {formatCurrency(acct.value)}
              </span>
            </Card>
          ))}
        </div>

        {/* Tax-Advantaged Contribution Tracker */}
        <Card className="lg:col-span-1">
          <h3 className="text-sm font-semibold text-text-primary mb-4">
            {new Date().getFullYear()} Contribution Tracker
          </h3>
          {contributionTrackers.map((t) => (
            <ProgressBar
              key={t.label}
              label={t.label}
              current={t.contributed}
              max={t.limit}
              color={
                t.limit == null
                  ? 'bg-accent'
                  : t.contributed / t.limit > 0.8
                  ? 'bg-success'
                  : t.contributed / t.limit > 0.5
                  ? 'bg-accent'
                  : 'bg-warning'
              }
            />
          ))}
        </Card>
      </div>

      {/* ── Asset Breakdown Row ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 kpi-grid">
        {/* Geographic Allocation */}
        <Card>
          <h3 className="text-sm font-semibold text-text-primary mb-4">
            Geographic Allocation
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={geoData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                  label={renderOuterPercentLabel}
                  labelLine={false}
                >
                  {geoData.map((_, index) => (
                    <Cell key={index} fill={['#2563EB', '#10B981', '#D97706', '#8B5CF6'][index % 4]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, color: '#8B949E' }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Sector Weights */}
        {sectorData.length > 0 ? (
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-4">
              Sector Breakdown
            </h3>
            <div className="space-y-2">
              {sectorData.map((s) => (
                <div key={s.name}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-text-secondary truncate mr-2">{s.name}</span>
                    <span className="text-xs font-mono text-text-primary whitespace-nowrap">{s.value.toFixed(1)}%</span>
                  </div>
                  <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all"
                      style={{ width: `${Math.min((s.value / (sectorData[0]?.value || 1)) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-4">
              Sector Breakdown
            </h3>
            <div className="flex items-center justify-center h-48 text-text-muted text-sm">
              {loadingHoldings ? 'Loading sector data…' : 'Sector data unavailable'}
            </div>
          </Card>
        )}

        {/* Top Underlying Holdings */}
        {topHoldingsData.length > 0 ? (
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-4">
              Underlying Holdings
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={topHoldingsData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                    label={renderOuterPercentLabel}
                    labelLine={false}
                  >
                    {topHoldingsData.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0];
                      return (
                        <div className="bg-bg-card border border-border rounded-lg px-3 py-2 shadow-lg">
                          <p className="text-text-primary text-sm font-medium">{d.name}</p>
                          <p className="text-text-secondary text-xs">{d.value.toFixed(2)}%</p>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        ) : (
          <Card>
            <h3 className="text-sm font-semibold text-text-primary mb-4">
              Underlying Holdings
            </h3>
            <div className="flex items-center justify-center h-48 text-text-muted text-sm">
              {loadingHoldings ? 'Loading holdings data…' : 'Holdings data unavailable'}
            </div>
          </Card>
        )}
      </div>

      {/* Holdings Table */}
      <Card>
        <h3 className="text-sm font-semibold text-text-primary mb-4">
          Holdings
        </h3>
        <Table>
          <Thead>
            <tr>
              <Th>Ticker</Th>
              <Th>Account</Th>
              <Th align="right">Shares</Th>
              <Th align="right">Cost Basis</Th>
              <Th align="right">Price</Th>
              <Th align="right">Value</Th>
              <Th align="right">Gain/Loss $</Th>
              <Th align="right">Gain/Loss %</Th>
              <Th align="right">Yield</Th>
              <Th align="center">Actions</Th>
            </tr>
          </Thead>
          <tbody>
            {investments.map((h) => {
              const isCash = /^(CASH|MARGIN)$/i.test(h.ticker);
              const isGain = h.gainLoss >= 0;

              if (isCash) {
                // Simplified row: single editable dollar value
                return (
                  <Tr key={h.id}>
                    <Td>
                      <span className="text-text-primary font-semibold">{h.ticker}</span>
                      <span className="text-text-muted text-xs block truncate max-w-[160px]">{h.name}</span>
                    </Td>
                    <Td><Badge variant="default">{h.account}</Badge></Td>
                    <Td colSpan={3} />
                    <InlineEditCell
                      value={h.currentValue}
                      displayValue={formatCurrency(h.currentValue)}
                      className="font-mono text-text-primary font-semibold"
                      onSave={(val) => {
                        // Set shares=val, costBasis=1, currentPrice=1 so currentValue = val
                        updateInvestment(h.id, { shares: val, costBasis: 1, currentPrice: 1 });
                        syncAccountBalance(h.account, { [h.id]: val });
                      }}
                    />
                    <Td align="right" className="font-mono text-text-muted">—</Td>
                    <Td align="right" className="font-mono text-text-muted">—</Td>
                    <Td align="right" className="font-mono text-text-muted">—</Td>
                    <Td align="center">
                      <button
                        onClick={() => handleDeleteHolding(h)}
                        className="p-1.5 rounded-md hover:bg-danger/20 text-text-muted hover:text-danger transition-colors"
                        title="Delete holding"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </Td>
                  </Tr>
                );
              }

              return (
                <Tr key={h.id}>
                  <Td>
                    <span className="text-text-primary font-semibold">
                      {h.ticker}
                    </span>
                    <span className="text-text-muted text-xs block truncate max-w-[160px]">
                      {h.name}
                    </span>
                  </Td>
                  <Td>
                    <Badge variant="default">{h.account}</Badge>
                  </Td>
                  <InlineEditCell
                    value={h.shares}
                    displayValue={formatNumber(h.shares, 2)}
                    className="font-mono text-text-primary"
                    onSave={(val) => handleSaveEdit(h.id, 'shares', val)}
                  />
                  <InlineEditCell
                    value={h.costBasis}
                    displayValue={formatCurrency(h.costBasis, 2)}
                    className="font-mono text-text-secondary"
                    onSave={(val) => handleSaveEdit(h.id, 'costBasis', val)}
                  />
                  <Td align="right" className="font-mono text-text-primary">
                    {formatCurrency(h.currentPrice, 2)}
                  </Td>
                  <Td align="right" className="font-mono text-text-primary font-semibold">
                    {formatCurrency(h.currentValue)}
                  </Td>
                  <Td
                    align="right"
                    className={`font-mono ${
                      isGain ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {formatCurrency(h.gainLoss)}
                  </Td>
                  <Td
                    align="right"
                    className={`font-mono ${
                      isGain ? 'text-success' : 'text-danger'
                    }`}
                  >
                    {formatPercent(h.gainLossPercent)}
                  </Td>
                  <Td align="right" className="font-mono text-text-secondary">
                    {h.yield.toFixed(2)}%
                  </Td>
                  <Td align="center">
                    <button
                      onClick={() => handleDeleteHolding(h)}
                      className="p-1.5 rounded-md hover:bg-danger/20 text-text-muted hover:text-danger transition-colors"
                      title="Delete holding"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </Tr>
              );
            })}
            {/* Holdings Subtotal */}
            <tr className="border-t-2 border-border bg-bg-tertiary/30">
              <Td className="font-bold text-text-primary" colSpan={1}>
                Holdings Total
              </Td>
              <Td />
              <Td />
              <Td align="right" className="font-mono font-bold text-text-secondary">
                {formatCurrency(totalCostBasis)}
              </Td>
              <Td />
              <Td align="right" className="font-mono font-bold text-text-primary">
                {formatCurrency(totalValue)}
              </Td>
              <Td
                align="right"
                className={`font-mono font-bold ${
                  totalGainLoss >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {formatCurrency(totalGainLoss)}
              </Td>
              <Td
                align="right"
                className={`font-mono font-bold ${
                  totalGainLossPct >= 0 ? 'text-success' : 'text-danger'
                }`}
              >
                {formatPercent(totalGainLossPct)}
              </Td>
              <Td align="right" className="font-mono font-bold text-text-secondary">
                {currentYield.toFixed(2)}%
              </Td>
              <Td />
            </tr>
            {/* Cash Accounts Row */}
            {balanceSheetCash > 0 && (
              <tr className="bg-bg-tertiary/15">
                <Td>
                  <span className="text-text-secondary font-medium text-xs">CASH ACCTS</span>
                  <span className="text-text-muted text-xs block">Checking & Savings</span>
                </Td>
                <Td>
                  <Badge variant="default">Bank Accounts</Badge>
                </Td>
                <Td />
                <Td />
                <Td />
                <Td align="right" className="font-mono text-text-primary font-semibold">
                  {formatCurrency(balanceSheetCash)}
                </Td>
                <Td align="right" className="font-mono text-text-muted">—</Td>
                <Td align="right" className="font-mono text-text-muted">—</Td>
                <Td />
                <Td />
              </tr>
            )}
            {/* Full Portfolio Total */}
            {balanceSheetCash > 0 && (
              <tr className="border-t-2 border-accent/30 bg-accent/5">
                <Td className="font-bold text-text-primary" colSpan={1}>
                  Full Portfolio
                </Td>
                <Td />
                <Td />
                <Td />
                <Td />
                <Td align="right" className="font-mono font-bold text-text-primary text-lg">
                  {formatCurrency(fullPortfolioValue)}
                </Td>
                <Td
                  align="right"
                  className={`font-mono font-bold ${
                    totalGainLoss >= 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  {formatCurrency(totalGainLoss)}
                </Td>
                <Td
                  align="right"
                  className={`font-mono font-bold ${
                    totalGainLossPct >= 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  {formatPercent(totalGainLossPct)}
                </Td>
                <Td />
                <Td />
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}
